import { useCallback, useEffect, useRef, useState, type DragEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import {
  defaultMaskSettings, defaultShadingSettings, newTemplateId, toHex, parseColor, MAX_PRINT_AREAS,
  type MaskSettings, type MockupKind, type MockupTemplate, type MockupView, type Point, type PrintArea, type Quad, type ShadingSettings,
} from '@eip/engine';
import { engine, transferBytes } from '../worker/client';
import { saveUserTemplate, templateStoreKind } from '../app/session';
import { commitDraft, Select, Slider, Toggle } from './controls';
import {
  activeArea, addArea, applyPrepared, canSave, commitAreaName, coverageWarning, downscaleNotice, editorToTemplate,
  fitPhotoSize, moveCorner, newEditorState, photoTooSmall, removeArea, renameArea, setActiveArea, setAreaSize,
  setView, unloadAfter, withPhoto, ADD_AREA_PRESETS, AREA_IN_MAX, AREA_IN_MIN, NOT_SEPARABLE_HINT,
  type AreaPreset, type EditorState, type PhotoSize,
} from '../lib/template-editor';
import { SESSION_ONLY_NOTICE } from '../lib/templates';
import { FOCUSABLE_SELECTOR, focusableFrom, nextFocusIndex } from '../lib/focus-trap';
import { Section } from './Section';

/** Every control change re-prepares the whole photo (mask flood + shading maps),
 *  so this is long enough to swallow a slider drag and short enough that a
 *  single click on a stepper still feels like it did something. */
export const PREPARE_DEBOUNCE_MS = 200;

/** The box the preview is fitted into, in CSS pixels. The canvas keeps the
 *  preview bitmap's own resolution (up to `MOCKUP_PREVIEW_MAX`) and is scaled
 *  down by CSS, so the mask outline stays crisp. */
const VIEW_W = 520, VIEW_H = 560;
/** Corner handle radius, in screen pixels. */
const HANDLE_R = 7;

const CORNERS: Array<keyof Quad> = ['tl', 'tr', 'br', 'bl'];
/** The "Add area" buttons, in the order they are offered. */
const PRESET_LABELS: Record<AreaPreset, string> = { sleeve: 'Sleeve', pocket: 'Pocket', custom: 'Custom' };
/** Said beside the file input once a blank has more than its primary area: choosing
 *  another photo is a different garment, and every quad on this one goes with it. */
export const PHOTO_RESETS_AREAS = 'Choosing a new photo resets the print areas.';
const VIEW_OPTIONS = [{ value: 'front', label: 'Front' }, { value: 'back', label: 'Back' }];
const KIND_OPTIONS = [{ value: 'flat', label: 'Flat lay' }, { value: 'model', label: 'On a model' }];
const METHOD_OPTIONS = [{ value: 'background', label: 'Background' }, { value: 'garment', label: 'Garment' }];

/** The photo's own size, before the worker caps it. Failing is not fatal: the
 *  worker decodes the same bytes and refuses a photo it cannot use. */
async function measure(blob: Blob): Promise<PhotoSize | null> {
  try {
    const bitmap = await createImageBitmap(blob);
    try { return { width: bitmap.width, height: bitmap.height }; } finally { bitmap.close(); }
  } catch { return null; }
}

/**
 * The template editor: a modal over the app that authors one blank. The photo
 * and its maps live in the worker under a transient editor id for as long as
 * this component is mounted; `mockupSaveEditor` moves them under the template's
 * own id on save, and the unmount cleanup drops them otherwise.
 *
 * Everything on screen comes from ONE `mockupPrepareEditor` call per change:
 * the preview bitmap, the cyan mask outline over it, the coverage and
 * separability readings, and the print area the first call derives. Results
 * carry a token — a response that lands after a newer request started is closed
 * and ignored, which matters because each one holds two multi-megabyte bitmaps.
 */
export function TemplateEditor({ seed, seedPhoto, styles, onCancel, onSaved }: {
  /** The template to start from (a duplicate, already given a fresh `u-` id, or
   *  a user template being re-edited), or null for a blank editor. */
  seed: MockupTemplate | null;
  /** That template's photo, already fetched by the caller. */
  seedPhoto: Blob | null;
  /** Known style slugs, offered as a `<datalist>` so a second blank of a style
   *  joins the one already in the picker instead of starting a group of its own. */
  styles: string[];
  onCancel(): void;
  onSaved(template: MockupTemplate): void;
}) {
  const [state, setState] = useState<EditorState>(() => newEditorState(seed ?? undefined));
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(seedPhoto);
  const [natural, setNatural] = useState<PhotoSize | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState(false);
  /** Bumped when new bitmaps land; the only thing the draw effect watches. */
  const [frame, setFrame] = useState(0);

  // `state` is mirrored into a ref because the debounce, the drag and the worker
  // callbacks all read the CURRENT editor rather than the one their closure was
  // created with. Every write goes through `commit`, so the two cannot drift.
  const stateRef = useRef(state);
  const busyRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  /** The dialog itself, whose Tab stops are the only ones the trap allows. */
  const dialogRef = useRef<HTMLDivElement | null>(null);
  /**
   * What had focus when the editor opened ("New template…", or "Edit" beside a
   * user template), so closing can put it back where the user left it.
   *
   * Read during the FIRST RENDER rather than in the mount effect: React applies
   * the name field's `autoFocus` while it commits the DOM, which is before any
   * effect runs, so an effect asking "what has focus?" would already be told
   * "the dialog's own name field" and the opener would be lost.
   */
  const openerRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null,
  );
  /** The photo picker, hidden and clicked by the button beside it. */
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  /** An untouched copy of the preview (no mask outline over it) for the eyedropper. */
  const sampleRef = useRef<HTMLCanvasElement | null>(null);
  const bitmapsRef = useRef<{ preview: ImageBitmap; maskEdge: ImageBitmap } | null>(null);
  const tokenRef = useRef(0);
  /** The preparation in flight, so a cancel can wait for it: the worker records
   *  the editor entry only after it has decoded the photo. */
  const inflightRef = useRef<Promise<boolean> | null>(null);
  /** Armed while a debounced re-prepare is pending — `undefined` once it fires. */
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closedRef = useRef(false);
  /** A corner drag in progress, the photo-space point the next frame will write,
   *  and the frame that will write it (declared here so the unmount cleanup can
   *  cancel a pending one). */
  const dragRef = useRef<{ pointerId: number; corner: keyof Quad } | null>(null);
  const pendingRef = useRef<Point | null>(null);
  const frameRef = useRef<number | null>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  /** Mirrors `saving` for the window-level Escape handler, which is bound once
   *  at mount and would otherwise read the flag its closure was created with. */
  const savingRef = useRef(false);
  savingRef.current = saving;

  const commit = useCallback((next: EditorState) => { stateRef.current = next; setState(next); }, []);

  const closeBitmaps = useCallback(() => {
    bitmapsRef.current?.preview.close();
    bitmapsRef.current?.maskEdge.close();
    bitmapsRef.current = null;
  }, []);

  /** Give the worker back the photo and maps it holds under this editor id. */
  const dropEditor = useCallback(() => engine().mockupUnload(stateRef.current.editorId), []);

  /**
   * One preparation, true when its result was adopted. `bytes` null re-prepares
   * the photo the worker already holds under this editor id — which is every
   * control change; only a newly chosen photo sends pixels.
   */
  const runPrepare = useCallback(async (bytes: Uint8Array | null): Promise<boolean> => {
    const token = ++tokenRef.current;
    setBusy(true); busyRef.current = true;
    setError(null);
    try {
      const result = await engine().mockupPrepareEditor(stateRef.current.editorId, bytes, stateRef.current.opts);
      // Superseded (a newer change) or gone (the modal closed): these two
      // bitmaps are ours to free and nothing will ever draw them.
      if (closedRef.current || tokenRef.current !== token) {
        result.preview.close(); result.maskEdge.close();
        // Closed means the cleanup has already posted its unload — and THIS
        // call has just (re-)created the entry in the worker. Drop it again.
        if (closedRef.current) void unloadAfter(null, dropEditor);
        return false;
      }
      const { preview, maskEdge, ...prepared } = result;
      closeBitmaps();
      bitmapsRef.current = { preview, maskEdge };
      commit(applyPrepared(stateRef.current, prepared));
      setFrame((n) => n + 1);
      return true;
    } catch (e) {
      if (closedRef.current || tokenRef.current !== token) return false;
      setError((e as Error).message);
      // A photo that failed on the way in never reached the worker (the throw
      // is inside `decodePhoto`, before the entry is recorded), so every later
      // control change would re-prepare "the photo already there" and fail with
      // `editor has no photo`. Forget it: the editor asks for another instead.
      if (bytes) { setPhotoBlob(null); setNatural(null); commit({ ...stateRef.current, photo: null, prepared: null }); }
      return false;
    } finally {
      if (!closedRef.current && tokenRef.current === token) { setBusy(false); busyRef.current = false; }
    }
  }, [closeBitmaps, commit, dropEditor]);

  /** `runPrepare`, with the promise kept where the unmount can wait on it. */
  const prepare = useCallback((bytes: Uint8Array | null): Promise<boolean> => {
    const pending = runPrepare(bytes);
    inflightRef.current = pending;
    void pending.finally(() => { if (inflightRef.current === pending) inflightRef.current = null; });
    return pending;
  }, [runPrepare]);

  const schedule = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      // A preparation in flight is waited out rather than raced: the first one
      // for a photo is the only one carrying the pixels, and re-arming is also
      // what lets an edit made during it reach the worker afterwards. With
      // nothing in flight and no photo there is simply nothing to prepare.
      if (busyRef.current) { schedule(); return; }
      if (!stateRef.current.photo) return;
      void prepare(null);
    }, PREPARE_DEBOUNCE_MS);
  }, [prepare]);

  /** Every edit: writes the state and, for anything the worker reads, re-prepares. */
  const edit = useCallback((fn: (s: EditorState) => EditorState, reprepare = false) => {
    commit(fn(stateRef.current));
    if (reprepare) schedule();
  }, [commit, schedule]);

  const setMask = (patch: Partial<MaskSettings>) =>
    edit((s) => ({ ...s, dirty: true, opts: { ...s.opts, mask: { ...s.opts.mask, ...patch } } }), true);
  const setShading = (patch: Partial<ShadingSettings>) =>
    edit((s) => ({ ...s, dirty: true, opts: { ...s.opts, shading: { ...s.opts.shading, ...patch } } }), true);

  // ---- the areas' inch fields -----------------------------------------------

  /**
   * The in-progress text of the inch fields, keyed `${areaId}:w` / `${areaId}:h`.
   * A field with no entry here shows its area's committed size, which is
   * `nextDraft`'s focus rule made structural: only the field being typed into
   * holds text of its own, and it drops the draft the moment it is committed.
   *
   * Committing per keystroke is what this replaces: `<input type="number">` reports
   * a half-typed "3." as an empty value, so "3.5" committed 3, then 35 — the field
   * was rewritten under the caret between the two keystrokes.
   */
  const [inchDrafts, setInchDrafts] = useState<Record<string, string>>({});
  const putDraft = (key: string, raw: string) => setInchDrafts((d) => ({ ...d, [key]: raw }));
  const dropDraft = (key: string) => setInchDrafts((d) => {
    if (!(key in d)) return d;
    const next = { ...d };
    delete next[key];
    return next;
  });
  /** Blur, Enter: the typed text becomes inches. Inches do not move the quad and
   *  nothing in the prepared maps reads them, so this never re-prepares. */
  const commitInches = (area: PrintArea, field: 'w' | 'h', raw: string) => {
    const id = area.id ?? '';
    const current = field === 'w' ? area.widthIn : area.heightIn;
    const v = commitDraft(raw, current, AREA_IN_MIN, AREA_IN_MAX);
    dropDraft(`${id}:${field}`);
    if (v !== current) edit((s) => setAreaSize(s, id, field === 'w' ? v : area.widthIn, field === 'h' ? v : area.heightIn));
  };
  // A row that has gone — removed, or replaced with the photo — must not leave its
  // text behind for whatever area lands on that key next.
  const areaIds = state.areas.map((a) => a.id ?? '').join('\u0000');
  useEffect(() => { setInchDrafts((d) => (Object.keys(d).length ? {} : d)); }, [areaIds]);

  // ---- the photo ------------------------------------------------------------

  const takePhoto = useCallback(async (blob: Blob, name: string, seeded = false) => {
    setError(null);
    const size = await measure(blob);
    if (closedRef.current) return;
    // Against the FITTED size: the worker downscales before it measures, so a
    // 9000 x 600 strip is judged at the 3000 x 200 it will actually be.
    const tooSmall = size ? photoTooSmall(fitPhotoSize(size.width, size.height)) : null;
    if (tooSmall) { setError(tooSmall); return; }
    setNatural(size);
    setPhotoBlob(blob);
    commit(withPhoto(stateRef.current, size, name, seeded));
    const bytes = new Uint8Array(await blob.arrayBuffer());
    if (closedRef.current) return;
    await prepare(transferBytes(bytes));
  }, [commit, prepare]);

  // Mount: prepare the seed's photo, and — whatever happens — give the worker
  // its editor entry back on the way out. The bytes are read inside the effect
  // rather than captured outside it so a second mount (StrictMode) transfers a
  // buffer of its own rather than the neutered one the first run sent.
  useEffect(() => {
    closedRef.current = false;
    if (seedPhoto) void takePhoto(seedPhoto, seed?.photo.src ?? 'photo', true);
    return () => {
      closedRef.current = true;
      clearTimeout(timerRef.current);
      if (frameRef.current !== null) { cancelAnimationFrame(frameRef.current); frameRef.current = null; }
      closeBitmaps();
      // Harmless after a save (`mockupSaveEditor` already dropped the entry) and
      // the only thing that frees the photo when the modal is closed any other
      // way — including the panel remounting under it on a document switch.
      // Chained onto the preparation in flight: cancelling during the first one
      // would otherwise unload before the worker had even recorded the entry.
      void unloadAfter(inflightRef.current, dropEditor);
    };
    // Mount/unmount only, deliberately: `seed`/`seedPhoto` are fixed for the
    // life of one modal (the panel mounts a new one to change them), and
    // re-running this would unload the photo out from under the editor.
  }, []);

  // Escape cancels, like the button does — and, like the button, is ignored
  // while a save is in flight: the save has already written to IndexedDB and is
  // mid-way through the registry and the worker (see `saveUserTemplate`'s
  // ordering), so unmounting here would post the editor's unload into the
  // middle of it and half-land the template.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !savingRef.current) cancelRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- focus (audit #16) ----------------------------------------------------
  //
  // On open: put focus in the dialog. The name field's `autoFocus` is what
  // usually does it, but that is a commit-time hint the browser is free to
  // ignore (a field inside a section that renders empty first, a re-mount under
  // StrictMode), so this checks and does it explicitly — the trap's first stop,
  // whichever control that is.
  //
  // On close, whichever way it closes — Escape, Cancel, Save — focus goes back
  // to the opener. That is the other half of the trap: without it, focus falls
  // to <body> and the next Tab starts at the top of the app, which is precisely
  // the walk the audit measured.
  useEffect(() => {
    const root = dialogRef.current;
    if (root && !root.contains(document.activeElement)) focusableFrom(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))[0]?.focus();
    return () => {
      const back = openerRef.current;
      // `isConnected`: a save can re-render the panel that held the opener (a new
      // user template appears in "My templates"), and focusing a detached node
      // would silently drop focus to <body> instead.
      if (back?.isConnected) back.focus();
    };
    // Open/close only: `seed`/`seedPhoto` are fixed for the life of one modal.
  }, []);

  /**
   * Tab and Shift+Tab, answered by the dialog rather than by the browser: the
   * stops are recomputed on every press (sections fold, sliders come and go with
   * their "Auto" toggles, "Save template" enables itself) and the walk wraps
   * inside `.template-editor`, so the app behind the backdrop is unreachable
   * until the editor closes.
   */
  const onTrapKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
    const root = dialogRef.current;
    if (!root) return;
    const stops = focusableFrom(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const next = stops[nextFocusIndex(stops.indexOf(document.activeElement as HTMLElement), stops.length, e.shiftKey)];
    if (!next) return;
    // Every Tab inside the dialog is ours, not only the one at either end: the
    // browser's own next stop and ours agree in the middle of the list, and
    // taking the press unconditionally is what makes the cycle the same one
    // whether or not a control between them has just been disabled.
    e.preventDefault();
    next.focus();
  };

  // ---- drawing --------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current, b = bitmapsRef.current;
    if (!canvas || !b) return;
    canvas.width = b.preview.width; canvas.height = b.preview.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(b.preview, 0, 0);
    ctx.drawImage(b.maskEdge, 0, 0);
    // The eyedropper samples the garment, not the outline drawn over it.
    const sample = sampleRef.current ?? (sampleRef.current = document.createElement('canvas'));
    sample.width = b.preview.width; sample.height = b.preview.height;
    sample.getContext('2d')?.drawImage(b.preview, 0, 0);
  }, [frame]);

  // ---- the corner handles ---------------------------------------------------

  /** Client coordinates → photo pixels, read off the overlay's own box so any
   *  CSS sizing (or a zoomed browser) still lands the corner under the pointer. */
  const toPhoto = useCallback((clientX: number, clientY: number): Point | null => {
    const svg = svgRef.current, photo = stateRef.current.photo;
    if (!svg || !photo) return null;
    const r = svg.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return [((clientX - r.left) / r.width) * photo.width, ((clientY - r.top) / r.height) * photo.height];
  }, []);

  const flushDrag = useCallback(() => {
    const to = pendingRef.current;
    pendingRef.current = null;
    const drag = dragRef.current, s = stateRef.current;
    if (!to || !drag) return;
    // The ACTIVE area: the handles are only ever drawn on that one. `moveCorner`
    // refuses a fold (or an area that has gone) by handing the state straight
    // back, which is also what skips the re-render for this frame.
    const next = moveCorner(s, s.activeAreaId, drag.corner, to);
    if (next === s) return;
    commit(next);
  }, [commit]);

  const onHandleDown = (e: ReactPointerEvent<SVGCircleElement>, corner: keyof Quad) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, corner };
  };
  const onHandleMove = (e: ReactPointerEvent<SVGCircleElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const to = toPhoto(e.clientX, e.clientY);
    if (!to) return;
    // One store write per frame: a drag emits pointermove far faster than the
    // quad can be redrawn, and each write re-renders the whole modal.
    pendingRef.current = to;
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => { frameRef.current = null; flushDrag(); });
  };
  const onHandleUp = (e: ReactPointerEvent<SVGCircleElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (frameRef.current !== null) { cancelAnimationFrame(frameRef.current); frameRef.current = null; }
    flushDrag();
    dragRef.current = null;
    // Once per gesture, not once per frame: the maps are rebuilt from the new
    // area, which is seconds of worker time on a large photo. Any debounce armed
    // by an earlier control change is folded into this one.
    clearTimeout(timerRef.current); timerRef.current = undefined;
    if (stateRef.current.photo) void prepare(null);
  };

  // ---- the eyedropper -------------------------------------------------------

  const onCanvasClick = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    if (!pick) return;
    const s = stateRef.current, b = bitmapsRef.current, sample = sampleRef.current;
    const to = s.photo && b && sample ? toPhoto(e.clientX, e.clientY) : null;
    if (!to || !s.photo || !b || !sample) return;
    const x = Math.min(b.preview.width - 1, Math.max(0, Math.round(to[0] * (b.preview.width / s.photo.width))));
    const y = Math.min(b.preview.height - 1, Math.max(0, Math.round(to[1] * (b.preview.height / s.photo.height))));
    const data = sample.getContext('2d')?.getImageData(x, y, 1, 1).data;
    if (!data) return;
    edit((cur) => ({ ...cur, dirty: true, garmentColor: [data[0], data[1], data[2]] }));
    setPick(false);
  };

  // ---- saving ---------------------------------------------------------------

  const onSave = async () => {
    const reason = canSave(stateRef.current);
    if (reason || !photoBlob) { setError(reason ?? 'Choose a photo'); return; }
    // A debounced re-prepare still armed means the settings on screen are newer
    // than the ones the worker measured — and `recolorable` IS that measurement.
    // Run it now rather than letting it fire into an editor entry
    // `mockupSaveEditor` has already handed over (which would fail for nothing).
    const armed = timerRef.current !== undefined;
    clearTimeout(timerRef.current); timerRef.current = undefined;
    setSaving(true); setError(null);
    if (armed && !(await prepare(null))) { setSaving(false); return; }
    try {
      // Minted ONCE and kept: a save that fails half-way and is retried must
      // land under the id the first attempt used, not a second one beside it.
      const id = stateRef.current.templateId ?? newTemplateId('u');
      if (stateRef.current.templateId !== id) commit({ ...stateRef.current, templateId: id });
      const template = editorToTemplate(stateRef.current, id);
      // What comes back may differ from what went in: `mockupSaveEditor`
      // re-prepares when the saved settings moved, and that can flip `separable`.
      const saved = await saveUserTemplate(stateRef.current.editorId, template, photoBlob);
      // The parent unmounts us from here; nothing below may touch state.
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  };

  // ---- render ---------------------------------------------------------------

  const mask: MaskSettings = { ...defaultMaskSettings(state.kind), ...state.opts.mask };
  const shading: ShadingSettings = { ...defaultShadingSettings(), ...state.opts.shading };
  const photo = state.photo;
  const scale = photo ? Math.min(1, VIEW_W / photo.width, VIEW_H / photo.height) : 1;
  const displayW = photo ? Math.max(1, Math.round(photo.width * scale)) : 0;
  const displayH = photo ? Math.max(1, Math.round(photo.height * scale)) : 0;
  const kx = photo ? displayW / photo.width : 1;
  const ky = photo ? displayH / photo.height : 1;
  const active = activeArea(state);
  const quad = active?.quad ?? null;
  const reason = canSave(state);
  const coverage = coverageWarning(state);
  const downscaled = downscaleNotice(natural, state.prepared);
  // `prepared` and the bitmaps land together, so this is also "there is a
  // preview to draw" — the draw effect runs on the same commit as this render.
  const shown = state.prepared !== null;
  /** The loaded photo's name, for the line beside "Choose photo…". A seeded
   *  template's `photoSrc` is a path inside the template folder, of which only
   *  the file name is news; a photo the user picked is already just a name. */
  const photoName = state.photo ? (state.photoSrc.split('/').pop() || 'Photo loaded') : 'No photo yet';

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void takePhoto(file, file.name);
  };

  return (
    <div className="modal-backdrop">
      <div
        ref={dialogRef} className="template-editor" role="dialog" aria-modal="true" aria-label="Template editor"
        onKeyDown={onTrapKeyDown}
      >
        <div className="editor-photo" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
          <h2>Template editor</h2>
          <div className="row">
            {/* The app's own button over a raw `<input type="file">` (audit #15):
                the native one rendered "Choose File / No file chosen" in the
                browser's light theme, unlike the "Open files…" button the rest of
                the app opens files with. The input keeps its `aria-label`, so it
                is still addressable by name — which is how a test hands it a file. */}
            <input
              ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/webp" aria-label="Shirt photo" hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first, so re-picking the same file after a refusal
                // ("photo too small for a template") fires `change` again.
                e.target.value = '';
                if (file) void takePhoto(file, file.name);
              }}
            />
            <button type="button" className="btn-secondary" onClick={() => photoInputRef.current?.click()}>Choose photo…</button>
            {/* What is loaded, beside the button that changes it: the native input
                said so itself, and dropping it without saying would have lost the
                one thing it did well. */}
            <span className="muted small">{photoName}</span>
            {/* Only with something to lose: a different photo is a different garment,
                so every quad on this one — sleeves and pockets included — goes with it. */}
            {state.areas.length > 1 && <span className="muted small">{PHOTO_RESETS_AREAS}</span>}
          </div>
          {shown && photo ? (
            <div className="editor-canvas-wrap" style={{ width: displayW, height: displayH }}>
              <canvas
                ref={canvasRef} className="editor-canvas" style={{ width: displayW, height: displayH, cursor: pick ? 'crosshair' : 'default' }}
                aria-label="Template photo" onClick={onCanvasClick}
              />
              <svg ref={svgRef} className="editor-handles" width={displayW} height={displayH}>
                {/* Every OTHER area: grey, dashed and named, so "which one is the
                    sleeve?" is answerable without counting quads. Only the active
                    one gets handles — four draggable dots per area would be a
                    thicket, and a drag would have to guess which area it meant. */}
                {state.areas.filter((a) => a !== active).map((a) => (
                  <g key={a.id}>
                    <polygon
                      className="quad-outline area-outline"
                      points={CORNERS.map((c) => `${a.quad[c][0] * kx},${a.quad[c][1] * ky}`).join(' ')}
                    />
                    {/* Just above the area's top-left corner — held inside the
                        overlay, or an area near the top edge would be labelled
                        off-canvas. */}
                    <text className="area-label" x={a.quad.tl[0] * kx} y={Math.max(11, a.quad.tl[1] * ky - 4)}>{a.name}</text>
                  </g>
                ))}
                {quad && (
                  <polygon
                    className="quad-outline"
                    points={CORNERS.map((c) => `${quad[c][0] * kx},${quad[c][1] * ky}`).join(' ')}
                  />
                )}
                {quad && CORNERS.map((c) => (
                  <circle
                    key={c} className="quad-handle" role="button" aria-label={`Corner ${c}`}
                    cx={quad[c][0] * kx} cy={quad[c][1] * ky} r={HANDLE_R}
                    onPointerDown={(e) => onHandleDown(e, c)}
                    onPointerMove={onHandleMove}
                    onPointerUp={onHandleUp}
                    onPointerCancel={onHandleUp}
                  />
                ))}
              </svg>
            </div>
          ) : (
            <div className="editor-empty muted small">{photoBlob ? 'Preparing the photo…' : 'Choose or drop a shirt photo to start.'}</div>
          )}
          {shown && state.areas.length > 0 && (
            <div className="editor-areas">
              <div className="row">
                <strong className="small">Print areas</strong>
                <span className="muted small">Drag the four corners of the active area on the photo.</span>
              </div>
              {state.areas.map((a, i) => {
                const id = a.id ?? '';
                // Every control on the row is named after the area it belongs to:
                // with four rows up, "Width" alone cannot be acted on.
                const label = a.name || id;
                return (
                <div key={id} className="area-row">
                  {/* One radio group over the rows: the active area is the one the
                      handles are on, and exactly one is always chosen. */}
                  <label className="area-active small">
                    <input
                      type="radio" name="active-area" value={id} aria-label={`Active: ${label}`}
                      checked={id === state.activeAreaId}
                      onChange={() => edit((s) => setActiveArea(s, id))}
                    />
                    Active
                  </label>
                  {/* Typed through as-is; a blank one takes the name a save would give
                      it anyway once the field is left. */}
                  <input
                    className="area-name" aria-label={`Area name: ${label}`} value={a.name ?? ''}
                    onChange={(e) => edit((s) => renameArea(s, id, e.target.value))}
                    onBlur={() => edit((s) => commitAreaName(s, id))}
                    onKeyDown={(e) => { if (e.key === 'Enter') edit((s) => commitAreaName(s, id)); }}
                  />
                  {/* Inches, not pixels: they are what the quad MEANS at the press,
                      and nothing in the prepared maps reads them — so neither field
                      re-prepares (`needsReprepare` says the same). Typed text is held
                      in `inchDrafts` until it is committed. */}
                  <input
                    type="number" className="ctl-num" aria-label={`Width (${label})`} min={AREA_IN_MIN} max={AREA_IN_MAX} step={0.5}
                    value={inchDrafts[`${id}:w`] ?? String(a.widthIn)}
                    onChange={(e) => putDraft(`${id}:w`, e.target.value)}
                    onBlur={(e) => commitInches(a, 'w', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitInches(a, 'w', e.currentTarget.value);
                      else if (e.key === 'Escape') dropDraft(`${id}:w`);
                    }}
                  />
                  <span className="muted small">×</span>
                  <input
                    type="number" className="ctl-num" aria-label={`Height (${label})`} min={AREA_IN_MIN} max={AREA_IN_MAX} step={0.5}
                    value={inchDrafts[`${id}:h`] ?? String(a.heightIn)}
                    onChange={(e) => putDraft(`${id}:h`, e.target.value)}
                    onBlur={(e) => commitInches(a, 'h', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitInches(a, 'h', e.currentTarget.value);
                      else if (e.key === 'Escape') dropDraft(`${id}:h`);
                    }}
                  />
                  <span className="muted small">in</span>
                  {/* The primary area seeds the mask flood and is where a job prints
                      by default: a blank without one has nothing to print on. */}
                  <button
                    type="button" className="right" disabled={i === 0}
                    title={i === 0 ? 'The first area is the one a design prints in by default' : undefined}
                    onClick={() => edit((s) => removeArea(s, id), true)}
                  >
                    Remove
                  </button>
                </div>
                );
              })}
              <div className="row">
                <span className="muted small">Add area</span>
                {ADD_AREA_PRESETS.map((preset) => (
                  <button
                    key={preset} type="button" disabled={state.areas.length >= MAX_PRINT_AREAS}
                    onClick={() => edit((s) => addArea(s, preset), true)}
                  >
                    {PRESET_LABELS[preset]}
                  </button>
                ))}
              </div>
              {state.areas.length >= MAX_PRINT_AREAS && <div className="muted small">{MAX_PRINT_AREAS} areas is the most a template can carry.</div>}
            </div>
          )}
          {busy && <div className="muted small">Preparing…</div>}
          {error && <div className="error small">{error}</div>}
          {downscaled && <div className="muted small">{downscaled}</div>}
          {coverage && <div className="small warn-text">{coverage}</div>}
          {state.prepared && !state.prepared.separable && <div className="small warn-text">{NOT_SEPARABLE_HINT}</div>}
          {state.prepared && (
            <div className="muted small" data-ms={Math.round(state.prepared.ms)} title={`Prepared in ${Math.round(state.prepared.ms)} ms`}>
              Photo {state.prepared.width} × {state.prepared.height} px · prepared
            </div>
          )}
        </div>

        <div className="editor-controls">
          {/* `remember={false}` on all three: these are one dialog's scratch state, not a
              panel preference. Folded once and remembered, the next "New template…"
              opened with the Name field hidden and "Save template" disabled for a
              reason nobody could see. */}
          <Section id="editor-template" title="Template" remember={false}>
            <label className="ctl">
              <span className="ctl-label">Name</span>
              <input aria-label="Template name" autoFocus value={state.name} onChange={(e) => edit((s) => ({ ...s, name: e.target.value, dirty: true }))} />
            </label>
            <label className="ctl">
              <span className="ctl-label">Style</span>
              <input
                aria-label="Style" list="editor-styles" value={state.style}
                onChange={(e) => edit((s) => ({ ...s, style: e.target.value, dirty: true }))}
              />
              <datalist id="editor-styles">{styles.map((s) => <option key={s} value={s} />)}</datalist>
            </label>
            <label className="ctl">
              <span className="ctl-label">Colour name</span>
              <input aria-label="Colour name" value={state.colorName} onChange={(e) => edit((s) => ({ ...s, colorName: e.target.value, dirty: true }))} />
            </label>
            {/* `setView`, not a plain field write: the primary area of a back blank
                is called "Back", and the one every preparation derives is "Chest". */}
            <Select label="View" value={state.view} options={VIEW_OPTIONS} onChange={(v) => edit((s) => setView(s, v as MockupView))} />
            {/* `kind` picks the DEFAULT mask settings, so switching it re-seeds
                them: a flat lay and a photo on a model are floodable from
                opposite ends, and keeping the other one's numbers would make
                the switch look like it did nothing. */}
            <Select
              label="Photo" value={state.kind} options={KIND_OPTIONS}
              onChange={(v) => edit((s) => {
                const kind = v as MockupKind;
                return { ...s, kind, dirty: true, opts: { ...s.opts, kind, mask: defaultMaskSettings(kind) } };
              }, true)}
            />
            <label className="ctl">
              <span className="ctl-label">Garment colour</span>
              <input
                type="color" aria-label="Garment colour" value={toHex(state.garmentColor)}
                onChange={(e) => edit((s) => ({ ...s, garmentColor: parseColor(e.target.value), dirty: true }))}
              />
              <button type="button" className={pick ? 'primary' : ''} aria-pressed={pick} onClick={() => setPick(!pick)} disabled={!shown}>
                Pick from photo
              </button>
            </label>
          </Section>

          <Section id="editor-mask" title="Mask" remember={false}>
            <Select label="Method" value={mask.method} options={METHOD_OPTIONS} onChange={(v) => setMask({ method: v as MaskSettings['method'] })} />
            {/* Audit #15's four renames. Every one of these was the operation's own
                name — the jargon was thickest in the one screen a shop uses to make
                its own blank — so each says what it does to the cut-out instead, and
                carries the one line that was nowhere on this screen before. */}
            <Slider
              label="Colour match" hint="How close a pixel's colour must be to count as garment"
              value={mask.tolerance} min={0.01} max={0.5} step={0.01} onChange={(v) => setMask({ tolerance: v })}
            />
            <Slider
              label="Background tolerance" hint="How close a pixel must be to the backdrop to be cut away"
              value={mask.bgTolerance} min={0.01} max={0.3} step={0.01} onChange={(v) => setMask({ bgTolerance: v })}
            />
            <Slider
              label="Soften edge" hint="Fades the cut-out's edge over this many pixels"
              value={mask.featherPx} min={0} max={20} step={1} unit="px" onChange={(v) => setMask({ featherPx: v })}
            />
            <Slider
              label="Fill small holes" hint="Closes gaps in the mask up to this size"
              value={mask.closePx} min={0} max={20} step={1} unit="px" onChange={(v) => setMask({ closePx: v })}
            />
          </Section>

          <Section id="editor-shading" title="Shading" remember={false}>
            <Toggle label="Auto blur" checked={shading.blurPx === 'auto'} onChange={(v) => setShading({ blurPx: v ? 'auto' : 6 })} />
            {shading.blurPx !== 'auto' && (
              <Slider label="Blur" value={shading.blurPx} min={1} max={30} step={1} unit="px" onChange={(v) => setShading({ blurPx: v })} />
            )}
            <Slider label="Wrinkle" hint="How much the fabric's folds bend the print" value={Math.round(shading.wrinkle * 1000)} min={0} max={30} step={1} onChange={(v) => setShading({ wrinkle: v / 1000 })} />
            <Toggle label="Auto midtone" checked={shading.lmid === 'auto'} onChange={(v) => setShading({ lmid: v ? 'auto' : 0.5 })} />
            {shading.lmid !== 'auto' && (
              <Slider label="Midtone" value={Math.round(shading.lmid * 100)} min={5} max={95} step={1} unit="%" onChange={(v) => setShading({ lmid: v / 100 })} />
            )}
          </Section>

        </div>

        {/* The dialog's own row, spanning both columns, NOT the last thing in the
            scrolling controls column: at 1366x768 the editor is taller than the
            window, and Save and Cancel were the part that went off the bottom — the
            two buttons the whole dialog exists to reach. `.template-editor` is
            `grid-template-rows: minmax(0, 1fr) auto` with the columns above scrolling
            on their own, so this row is always on screen. */}
        <div className="section editor-actions">
          <button type="button" className="primary" disabled={!!reason || busy || saving} onClick={() => void onSave()}>
            {saving ? 'Saving…' : 'Save template'}
          </button>
          <button type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          {reason ? <span className="muted small">{reason}</span> : state.dirty ? <span className="muted small">Unsaved changes</span> : null}
          {/* Said before the shop spends ten minutes authoring a blank, not
              after the reload that loses it. */}
          {templateStoreKind() === 'memory' && <div className="muted small">{SESSION_ONLY_NOTICE}</div>}
        </div>
      </div>
    </div>
  );
}
